import { getRegistry } from '../registry'
import { injectTriggerStyles } from './trigger.css'
import type { DisplayMode, DisplayModeConfig, TriggerMountOptions } from '../types'

const CHAT_PATH = 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z'

function buildDefaultIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('aria-hidden', 'true')
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  p.setAttribute('d', CHAT_PATH)
  svg.appendChild(p)
  return svg
}

// Parse a trusted SVG/HTML markup string (per contract, icon-as-string is host-trusted) into DOM
// nodes without relying on innerHTML. We use DOMParser which treats the input as a fragment.
function parseTrustedMarkup(markup: string): DocumentFragment {
  const frag = document.createDocumentFragment()
  const trimmed = markup.trim()
  if (!trimmed) return frag

  if (trimmed.startsWith('<svg')) {
    try {
      const doc = new DOMParser().parseFromString(trimmed, 'image/svg+xml')
      const root = doc.documentElement
      if (root && root.nodeName.toLowerCase() === 'svg') {
        frag.appendChild(document.importNode(root, true))
        return frag
      }
    } catch { /* fall through */ }
  }

  try {
    const doc = new DOMParser().parseFromString(`<body>${trimmed}</body>`, 'text/html')
    const body = doc.body
    while (body.firstChild) frag.appendChild(document.importNode(body.firstChild, true))
  } catch {
    frag.appendChild(document.createTextNode(markup))
  }
  return frag
}

function resolveTarget(target: TriggerMountOptions['target']): { kind: 'menu' } | { kind: 'action'; id: string } {
  if (!target || target === 'menu') return { kind: 'menu' }
  return { kind: 'action', id: target.actionId }
}

function applyDisplayOverride(display: DisplayMode | DisplayModeConfig | undefined): void {
  if (!display) return
  const registry = getRegistry()
  if (typeof display === 'string') {
    registry.setDisplayMode(display)
  } else if (display.mode) {
    registry.setDisplayMode(display.mode, display.side)
  }
}

export function mountTrigger(
  target: string | HTMLElement,
  opts: TriggerMountOptions = {},
): { unmount(): void } {
  if (typeof document === 'undefined') {
    return { unmount() { /* noop */ } }
  }
  injectTriggerStyles()

  const element = typeof target === 'string' ? document.querySelector(target) : target
  if (!element) {
    throw new Error(`[reqdesk] mountTrigger: target not found (${String(target)})`)
  }

  const variant = opts.variant ?? 'pill'
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = `rqd-trigger rqd-trigger--${variant}${opts.className ? ` ${opts.className}` : ''}`
  if (opts.style) {
    for (const [k, v] of Object.entries(opts.style)) {
      if (v != null) (btn.style as unknown as Record<string, string>)[k] = String(v)
    }
  }

  if (opts.icon !== null) {
    if (typeof opts.icon === 'string') {
      btn.appendChild(parseTrustedMarkup(opts.icon))
    } else if (opts.icon instanceof HTMLElement) {
      btn.appendChild(opts.icon)
    } else {
      btn.appendChild(buildDefaultIcon())
    }
  }

  const label = opts.label ?? 'Open support'
  const labelSpan = document.createElement('span')
  labelSpan.className = 'rqd-trigger-label'
  labelSpan.textContent = label
  btn.appendChild(labelSpan)
  btn.setAttribute('aria-label', label)
  btn.setAttribute('aria-haspopup', 'menu')
  btn.setAttribute('aria-expanded', 'false')

  const registry = getRegistry()
  const targetResolved = resolveTarget(opts.target)

  const handleClick = (e: Event) => {
    registry.recordActivator(btn)
    applyDisplayOverride(opts.display)
    if (targetResolved.kind === 'menu') {
      registry.openMenu()
    } else {
      registry.openAction(targetResolved.id)
    }
    e.stopPropagation()
  }

  btn.addEventListener('click', handleClick)

  const unsubscribe = registry.subscribe((snap) => {
    btn.setAttribute('aria-expanded', String(snap.isOpen))
  })

  element.appendChild(btn)

  return {
    unmount() {
      unsubscribe()
      btn.removeEventListener('click', handleClick)
      if (btn.parentElement) btn.parentElement.removeChild(btn)
    },
  }
}
